/**
 * FSE PhoneInput (web) — single outlined field with `+27` prefix + auto-format.
 *
 * Design source: `Documentation/Claude design files/preview/input-phone.html`
 * and parity with the mobile surface `packages/ui/src/mobile/components/PhoneInput.tsx`.
 *
 * The caller stores + receives only the 9-digit local portion (e.g. "821234567").
 * The visible value is formatted as `82 123 4567` and `+27` renders as an inline
 * start adornment inside the same outlined field — the MD3 unified-input pattern,
 * not a sidecar chip. E.164 normalisation (`+27821234567`) happens at the API
 * boundary on submit, not here.
 *
 * Per FSE design this is the primary login surface: ~64dp tall, large numbers.
 * Public prop surface is FSE-owned; MUI's `sx` is never exposed.
 */
import {
  InputAdornment,
  TextField,
  Typography,
  type TextFieldProps,
} from '@mui/material';
import { forwardRef } from 'react';

export interface PhoneInputProps {
  /** Local-format ZA phone — 0-9 digits as typed (no spaces, no +27 prefix). */
  value: string;
  /** Receives the cleaned digits-only string (up to 9 chars, no spaces). */
  onChangeText: (cleaned: string) => void;
  /** Inline error message. Presence sets the field's error state. */
  error?: string;
  /** Field label. Default "Phone number". */
  label?: string;
  /** Helper caption shown below the field when there is no error. */
  hint?: string;
  /** Focus on mount. */
  autoFocus?: boolean;
  /** Disable input. */
  disabled?: boolean;
}

/**
 * Format up to 9 digits as the SA mobile pattern `XX XXX XXXX`.
 * Partials format gracefully (`82` → `82`, `8212` → `82 12`, …).
 */
function formatLocal(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 9);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)} ${d.slice(2)}`;
  return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
}

export const PhoneInput = forwardRef<HTMLDivElement, PhoneInputProps>(function PhoneInput(
  { value, onChangeText, error, label = 'Phone number', hint, autoFocus = false, disabled = false },
  ref,
) {
  const handleChange: NonNullable<TextFieldProps['onChange']> = (e) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 9);
    onChangeText(cleaned);
  };

  return (
    <TextField
      ref={ref}
      label={label}
      value={formatLocal(value)}
      onChange={handleChange}
      error={!!error}
      helperText={error || hint || undefined}
      disabled={disabled}
      autoFocus={autoFocus}
      fullWidth
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  // Mode-aware: match the field's typed-digit ink (MUI
                  // `text.primary`) so the +27 prefix flips with the color
                  // mode. In light this is the same ink as brand.earth[900].
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                +27
              </Typography>
            </InputAdornment>
          ),
        },
        htmlInput: {
          inputMode: 'numeric',
          autoComplete: 'tel',
          // 9 digits + 2 spaces = 11 visible chars
          maxLength: 11,
          'aria-label': label,
        },
      }}
      sx={{
        // Full-width + theme-default size/radius so the field matches its
        // sibling fields (e.g. the PIN field on the login form). The +27
        // adornment and auto-formatting are what make this a PhoneInput —
        // not an oversized "primary surface" treatment.
        '& .MuiOutlinedInput-root': {
          fontVariantNumeric: 'tabular-nums',
        },
      }}
    />
  );
});
